// Per-deployment tenant config. One Docker image serves many tenants; the
// running tenant is selected at runtime via TENANT and exposed to
// the client through /api/features. This file is the source of truth for which
// retailers a tenant shows, its branding (logo/colors/name), copy, and flow toggles.

import type { PanelId } from './panels.js';

export type TenantCopy = {
  documentTitle: string; // <title> / document.title
  landingTitle: string;
  landingTitleHighlight?: string; // trailing substring of landingTitle shown in --retailer-primary; '' / unset = no highlight
  landingLede: string;
  landingLedeBold?: string; // substring of landingLede shown bold (not colored); '' / unset = no bold
  regionTitle: string;
  regionSubtitle: string;
  signinSecurityNote: string; // shown under the sign-in card; '' = hidden
  confirmationCodeLabel: string;
  confirmationCodeHint: string; // shown under the code digits; '' = hidden
  rewardEmailLabel: string; // label above the gift-card email input; only used when flow.requireEmailCapture
};

export type TenantConfig = {
  id: string;
  appName: string; // logo alt text
  headerLogo: string; // logo shown in the app header (public/ URL)
  pageLogo: string; // logo shown on Dashboard / Error pages (public/ URL)
  theme: Record<string, string>; // CSS var overrides applied at runtime
  enabledRetailers: string[]; // retailer_keys the selector shows
  copy: TenantCopy;
  flow: {
    showConfirmationCode: boolean;
    viewData: boolean;
    hideHeader: boolean;
    chromeless: boolean; // strips header + footer, tweaks copy for embedded views
    requireEmailCapture: boolean; // landing page collects+persists a gift-card email before "Get Started" continues
  };
  googleClientId?: string;
  domains?: string[]; // hostnames that resolve to this tenant (see getTenantIdForHostname)
};

const DEFAULT_TENANT: TenantConfig = {
  id: 'default',
  appName: 'Tap',
  headerLogo: '/logos/tap-logo.png',
  pageLogo: '/logos/app-logo.svg',
  theme: {},
  enabledRetailers: [
    'amazon_us',
    'amazon_ca',
    'safeway',
    'doordash',
    'bestbuy',
    'target',
    'gmail',
  ],
  copy: {
    documentTitle: 'Tap',
    landingTitle: 'Link your store account',
    landingLede:
      "We're inviting a group of participants to share their purchase history — it unlocks future survey opportunities and helps brands understand real shopping habits.",
    regionTitle: 'Where do you shop?',
    regionSubtitle: 'Select your store below.',
    signinSecurityNote: '',
    confirmationCodeLabel: 'Your confirmation code',
    confirmationCodeHint:
      'Enter this code back in your survey to confirm your connection.',
    rewardEmailLabel: '',
  },
  flow: {
    showConfirmationCode: false,
    viewData: true,
    hideHeader: false,
    chromeless: false,
    requireEmailCapture: false,
  },
};

const TENANTS: Record<string, TenantConfig> = {
  default: DEFAULT_TENANT,
  clorox: {
    id: 'clorox',
    domains: ['clorox.corelens.ai'],
    appName: 'Clorox',
    headerLogo: '/logos/clorox-logo.svg',
    pageLogo: '/logos/clorox-logo.svg',
    theme: {
      '--retailer-primary': '#0061c1',
      '--retailer-primary-deep': '#004b96',
      '--retailer-primary-pale': '#e7f1fc',
      '--retailer-ink': '#0a1430',
      '--retailer-ink-soft': '#1f2a44',
      '--retailer-ink-muted': '#5b6577',
      '--retailer-ink-faint': '#98a0af',
      '--retailer-surface': '#ffffff',
      '--retailer-paper': '#f7f9fc',
      '--retailer-paper-warm': '#f1f4f9',
      '--retailer-rule': '#e2e7ef',
      '--retailer-rule-light': '#eef1f6',
      '--retailer-success': '#16a34a',
      '--retailer-success-pale': '#ecfdf5',
    },
    enabledRetailers: ['amazon_us', 'amazon_ca'],
    copy: {
      documentTitle: 'Clorox · Connect your Amazon account',
      landingTitle: 'Link your Amazon account',
      landingLede:
        'Clorox has invited you to share your Amazon purchase history. It helps our brands understand real shopping habits — and unlocks future reward opportunities for you.',
      regionTitle: 'Where do you shop?',
      regionSubtitle: 'Select your Amazon region below.',
      signinSecurityNote:
        'Your credentials are used once to establish a secure, read-only connection. Clorox never sees or stores your password.',
      confirmationCodeLabel: 'Your confirmation code',
      confirmationCodeHint:
        'Enter this code back in your survey to confirm your connection and qualify for rewards.',
      rewardEmailLabel: '',
    },
    flow: {
      showConfirmationCode: true,
      viewData: true,
      hideHeader: false,
      chromeless: false,
      requireEmailCapture: false,
    },
  },
  q11: {
    ...DEFAULT_TENANT,
    id: 'q11',
    domains: ['q11.corelens.ai'],
    copy: {
      ...DEFAULT_TENANT.copy,
      landingTitle: 'Share your Amazon e-receipts, get a $5 gift card',
      landingTitleHighlight: '$5 gift card',
      landingLede:
        "Link your Amazon account to share your e-receipts for research, and we'll thank you with a $5 Amazon gift card, delivered by email.",
      landingLedeBold: '$5 Amazon gift card',
      rewardEmailLabel: 'What email address should we send your gift card to?',
    },
    flow: {
      showConfirmationCode: true,
      viewData: false,
      hideHeader: true,
      chromeless: false,
      requireEmailCapture: true,
    },
  },
  // Selected client-side via `?view=ario` (see getTenantFromViewParam below)
  // rather than TENANT — used to embed the flow inside
  // Ario-internal tooling.
  ario: {
    ...DEFAULT_TENANT,
    id: 'ario',
    copy: {
      ...DEFAULT_TENANT.copy,
      landingLede:
        'Get $25 store credit and exclusive personalized perks at CircuitShack when you link your Amazon Account!',
    },
    flow: { ...DEFAULT_TENANT.flow, chromeless: true },
  },
  verify: {
    ...DEFAULT_TENANT,
    id: 'verify',
    domains: ['verify.corelens.ai'],
    copy: {
      ...DEFAULT_TENANT.copy,
      landingTitle: "You're eligible to join our Verified Buyer Panel.",
      landingTitleHighlight: 'Verified Buyer Panel.',
      landingLede:
        "Many of our customers want to hear from real verified buyers. Connect one or more of your retail accounts below to become eligible for upcoming verified buyer studies -- the more you connect, the more likely you'll be contacted.",
      regionTitle: 'Select account to connect',
      regionSubtitle: '',
    },
    flow: { ...DEFAULT_TENANT.flow, hideHeader: true },
  },
};

export function getTenant(id?: string | null): TenantConfig {
  if (!id) return DEFAULT_TENANT;
  return TENANTS[id.trim().toLowerCase()] ?? DEFAULT_TENANT;
}

// `?view=<tenantId>` lets any known tenant be selected client-side (e.g. for
// embedding inside partner tooling), independent of the server-side
// TENANT deployment config. Unknown/missing values return null
// so callers fall back to the server-resolved tenant instead of `default`.
export function getTenantFromViewParam(
  view?: string | null
): TenantConfig | null {
  if (!view) return null;
  return TENANTS[view.trim().toLowerCase()] ?? null;
}

// Maps a request hostname (e.g. `q11.corelens.ai`) to the tenant that owns it,
// so one process can serve multiple tenant domains. Falls back to null so
// callers can fall back to TENANT / default.
export function getTenantIdForHostname(
  hostname?: string | null
): string | null {
  if (!hostname) return null;
  const host = hostname.trim().toLowerCase();
  for (const t of Object.values(TENANTS)) {
    if (t.domains?.some((d) => d.toLowerCase() === host)) return t.id;
  }
  return null;
}

// Show the "Tap" name and icon only for real Tap Research traffic (#1377).
// The default tenant has no own domain, so Tap Research and Listen Labs
// both land here — without this check, Listen Labs would show "Tap" too.
const GENERIC_DOCUMENT_TITLE = 'CoreLens';
const GENERIC_FAVICON_URL = '/favicon-corelens.svg';
const TAP_RESEARCH_FAVICON_URL = '/favicon.svg';

export function resolveDocumentTitle(
  tenant: TenantConfig,
  panelId: PanelId | null
): string {
  if (tenant.id === 'default' && panelId !== 'tap_research') {
    return GENERIC_DOCUMENT_TITLE;
  }
  return tenant.copy?.documentTitle || tenant.appName;
}

export function resolveFaviconUrl(
  tenant: TenantConfig,
  panelId: PanelId | null
): string {
  if (tenant.id === 'default' && panelId !== 'tap_research') {
    return GENERIC_FAVICON_URL;
  }
  return TAP_RESEARCH_FAVICON_URL;
}
